import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Icons } from "@/components/icons";

export default function Overview() {
  // Demo data
  const stats = {
    totalCollected: 145750,
    pendingPayments: 23,
    thisMonth: 45200,
    completedToday: 8,
  };

  const recentPayments = [
    {
      id: "HAF001",
      donor: "Ahmed Al-Rashid",
      amount: 2500,
      status: "completed",
      timestamp: "2 hours ago",
    },
    {
      id: "HAF002",
      donor: "Fatima Hassan",
      amount: 1800,
      status: "pending",
      timestamp: "4 hours ago",
    },
    {
      id: "HAF003",
      donor: "Omar Abdullah",
      amount: 3200,
      status: "completed",
      timestamp: "6 hours ago",
    },
    {
      id: "HAF004",
      donor: "Aisha Mohammed",
      amount: 1500,
      status: "processing",
      timestamp: "1 day ago",
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">
          Dashboard Overview
        </h2>
        <p className="text-muted-foreground">
          Welcome to your HAF Collector dashboard
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Collected
            </CardTitle>
            <Icons.dollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              QR {stats.totalCollected.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              +12% from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Pending Payments
            </CardTitle>
            <Icons.clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pendingPayments}</div>
            <p className="text-xs text-muted-foreground">Requiring attention</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
            <Icons.calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              QR {stats.thisMonth.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">+8% from last month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Completed Today
            </CardTitle>
            <Icons.checkCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.completedToday}</div>
            <p className="text-xs text-muted-foreground">Payments processed</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Payments */}
      <Card>
        <CardHeader>
          <CardTitle>Recent HAF Payments</CardTitle>
          <CardDescription>
            Latest payment activities in your collection area
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentPayments.map((payment) => (
              <div
                key={payment.id}
                className="flex items-center justify-between border-b pb-4 last:border-b-0"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{payment.donor}</span>
                    <Badge variant="outline" className="text-xs">
                      {payment.id}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {payment.timestamp}
                  </p>
                </div>
                <div className="text-right">
                  <div className="font-medium">
                    QR {payment.amount.toLocaleString()}
                  </div>
                  <Badge
                    variant={
                      payment.status === "completed"
                        ? "default"
                        : payment.status === "pending"
                        ? "destructive"
                        : "secondary"
                    }
                    className="text-xs"
                  >
                    {payment.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common tasks for HAF collectors</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex flex-col items-center space-y-2 rounded-lg border p-4 hover:bg-accent cursor-pointer">
              <Icons.plus className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm font-medium">Record New Payment</span>
            </div>
            <div className="flex flex-col items-center space-y-2 rounded-lg border p-4 hover:bg-accent cursor-pointer">
              <Icons.search className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm font-medium">Search Payments</span>
            </div>
            <div className="flex flex-col items-center space-y-2 rounded-lg border p-4 hover:bg-accent cursor-pointer">
              <Icons.fileText className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm font-medium">Generate Report</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
